import * as i0 from '@angular/core';
import { Injectable, Directive, Input, NgModule } from '@angular/core';
import * as i1 from '@ngbracket/ngx-layout/core';
import { StyleBuilder, BaseDirective2, CoreModule } from '@ngbracket/ngx-layout/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_MAIN$1 = 'start';
const DEFAULT_CROSS$1 = 'stretch';
let GridAlignColumnsStyleBuilder = /*#__PURE__*/(() => {
  class GridAlignColumnsStyleBuilder extends StyleBuilder {
    buildStyles(input, parent) {
      return buildCss$2(input || `${DEFAULT_MAIN$1} ${DEFAULT_CROSS$1}`, parent.inline);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridAlignColumnsStyleBuilder_BaseFactory;
        return function GridAlignColumnsStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridAlignColumnsStyleBuilder_BaseFactory || (ɵGridAlignColumnsStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridAlignColumnsStyleBuilder)))(__ngFactoryType__ || GridAlignColumnsStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridAlignColumnsStyleBuilder,
        factory: GridAlignColumnsStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridAlignColumnsStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridAlignColumnsDirective = /*#__PURE__*/(() => {
  class GridAlignColumnsDirective extends BaseDirective2 {
    get inline() {
      return this._inline;
    }
    set inline(val) {
      this._inline = coerceBooleanProperty(val);
    }
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this.DIRECTIVE_KEY = 'grid-align-columns';
      this._inline = false;
      this.init();
    }
    // *********************************************
    // Protected methods
    // *********************************************
    updateWithValue(value) {
      this.styleCache = this.inline ? alignColumnsInlineCache : alignColumnsCache;
      this.addStyles(value, {
        inline: this.inline
      });
    }
    static {
      this.ɵfac = function GridAlignColumnsDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridAlignColumnsDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridAlignColumnsStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridAlignColumnsDirective,
        inputs: {
          inline: [0, "gdInline", "inline"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridAlignColumnsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const alignColumnsCache = new Map();
const alignColumnsInlineCache = new Map();
const inputs$a = ['gdAlignColumns', 'gdAlignColumns.xs', 'gdAlignColumns.sm', 'gdAlignColumns.md', 'gdAlignColumns.lg', 'gdAlignColumns.xl', 'gdAlignColumns.lt-sm', 'gdAlignColumns.lt-md', 'gdAlignColumns.lt-lg', 'gdAlignColumns.lt-xl', 'gdAlignColumns.gt-xs', 'gdAlignColumns.gt-sm', 'gdAlignColumns.gt-md', 'gdAlignColumns.gt-lg'];
const selector$a = `
  [gdAlignColumns],
  [gdAlignColumns.xs], [gdAlignColumns.sm], [gdAlignColumns.md],
  [gdAlignColumns.lg], [gdAlignColumns.xl], [gdAlignColumns.lt-sm],
  [gdAlignColumns.lt-md], [gdAlignColumns.lt-lg], [gdAlignColumns.lt-xl],
  [gdAlignColumns.gt-xs], [gdAlignColumns.gt-sm], [gdAlignColumns.gt-md],
  [gdAlignColumns.gt-lg]
`;
/**
 * 'column alignment' CSS Grid styling directive
 * Configures the alignment in the column direction
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-19
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-21
 */
let DefaultGridAlignColumnsDirective = /*#__PURE__*/(() => {
  class DefaultGridAlignColumnsDirective extends GridAlignColumnsDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$a;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridAlignColumnsDirective_BaseFactory;
        return function DefaultGridAlignColumnsDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridAlignColumnsDirective_BaseFactory || (ɵDefaultGridAlignColumnsDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridAlignColumnsDirective)))(__ngFactoryType__ || DefaultGridAlignColumnsDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridAlignColumnsDirective,
        selectors: [["", "gdAlignColumns", ""], ["", "gdAlignColumns.xs", ""], ["", "gdAlignColumns.sm", ""], ["", "gdAlignColumns.md", ""], ["", "gdAlignColumns.lg", ""], ["", "gdAlignColumns.xl", ""], ["", "gdAlignColumns.lt-sm", ""], ["", "gdAlignColumns.lt-md", ""], ["", "gdAlignColumns.lt-lg", ""], ["", "gdAlignColumns.lt-xl", ""], ["", "gdAlignColumns.gt-xs", ""], ["", "gdAlignColumns.gt-sm", ""], ["", "gdAlignColumns.gt-md", ""], ["", "gdAlignColumns.gt-lg", ""]],
        inputs: {
          gdAlignColumns: "gdAlignColumns",
          "gdAlignColumns.xs": "gdAlignColumns.xs",
          "gdAlignColumns.sm": "gdAlignColumns.sm",
          "gdAlignColumns.md": "gdAlignColumns.md",
          "gdAlignColumns.lg": "gdAlignColumns.lg",
          "gdAlignColumns.xl": "gdAlignColumns.xl",
          "gdAlignColumns.lt-sm": "gdAlignColumns.lt-sm",
          "gdAlignColumns.lt-md": "gdAlignColumns.lt-md",
          "gdAlignColumns.lt-lg": "gdAlignColumns.lt-lg",
          "gdAlignColumns.lt-xl": "gdAlignColumns.lt-xl",
          "gdAlignColumns.gt-xs": "gdAlignColumns.gt-xs",
          "gdAlignColumns.gt-sm": "gdAlignColumns.gt-sm",
          "gdAlignColumns.gt-md": "gdAlignColumns.gt-md",
          "gdAlignColumns.gt-lg": "gdAlignColumns.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridAlignColumnsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function buildCss$2(align, inline) {
  const css = {},
    [mainAxis, crossAxis] = align.split(' ');
  // Main axis
  switch (mainAxis) {
    case 'center':
      css['align-content'] = 'center';
      break;
    case 'space-around':
      css['align-content'] = 'space-around';
      break;
    case 'space-between':
      css['align-content'] = 'space-between';
      break;
    case 'space-evenly':
      css['align-content'] = 'space-evenly';
      break;
    case 'end':
      css['align-content'] = 'end';
      break;
    case 'start':
      css['align-content'] = 'start';
      break;
    case 'stretch':
      css['align-content'] = 'stretch';
      break;
    default:
      // default main axis
      css['align-content'] = DEFAULT_MAIN$1;
      break;
  }
  // Cross-axis
  switch (crossAxis) {
    case 'start':
      css['align-items'] = 'start';
      break;
    case 'center':
      css['align-items'] = 'center';
      break;
    case 'end':
      css['align-items'] = 'end';
      break;
    case 'stretch':
      css['align-items'] = 'stretch';
      break;
    default:
      // 'stretch'
      // default cross axis
      css['align-items'] = DEFAULT_CROSS$1;
      break;
  }
  css['display'] = inline ? 'inline-grid' : 'grid';
  return css;
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_MAIN = 'start';
const DEFAULT_CROSS = 'stretch';
let GridAlignRowsStyleBuilder = /*#__PURE__*/(() => {
  class GridAlignRowsStyleBuilder extends StyleBuilder {
    buildStyles(input, parent) {
      return buildCss$1(input || `${DEFAULT_MAIN} ${DEFAULT_CROSS}`, parent.inline);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridAlignRowsStyleBuilder_BaseFactory;
        return function GridAlignRowsStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridAlignRowsStyleBuilder_BaseFactory || (ɵGridAlignRowsStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridAlignRowsStyleBuilder)))(__ngFactoryType__ || GridAlignRowsStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridAlignRowsStyleBuilder,
        factory: GridAlignRowsStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridAlignRowsStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridAlignRowsDirective = /*#__PURE__*/(() => {
  class GridAlignRowsDirective extends BaseDirective2 {
    get inline() {
      return this._inline;
    }
    set inline(val) {
      this._inline = coerceBooleanProperty(val);
    }
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this.DIRECTIVE_KEY = 'grid-align-rows';
      this._inline = false;
      this.init();
    }
    // *********************************************
    // Protected methods
    // *********************************************
    updateWithValue(value) {
      this.styleCache = this.inline ? alignRowsInlineCache : alignRowsCache;
      this.addStyles(value, {
        inline: this.inline
      });
    }
    static {
      this.ɵfac = function GridAlignRowsDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridAlignRowsDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridAlignRowsStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridAlignRowsDirective,
        inputs: {
          inline: [0, "gdInline", "inline"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridAlignRowsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const alignRowsCache = new Map();
const alignRowsInlineCache = new Map();
const inputs$9 = ['gdAlignRows', 'gdAlignRows.xs', 'gdAlignRows.sm', 'gdAlignRows.md', 'gdAlignRows.lg', 'gdAlignRows.xl', 'gdAlignRows.lt-sm', 'gdAlignRows.lt-md', 'gdAlignRows.lt-lg', 'gdAlignRows.lt-xl', 'gdAlignRows.gt-xs', 'gdAlignRows.gt-sm', 'gdAlignRows.gt-md', 'gdAlignRows.gt-lg'];
const selector$9 = `
  [gdAlignRows],
  [gdAlignRows.xs], [gdAlignRows.sm], [gdAlignRows.md],
  [gdAlignRows.lg], [gdAlignRows.xl], [gdAlignRows.lt-sm],
  [gdAlignRows.lt-md], [gdAlignRows.lt-lg], [gdAlignRows.lt-xl],
  [gdAlignRows.gt-xs], [gdAlignRows.gt-sm], [gdAlignRows.gt-md],
  [gdAlignRows.gt-lg]
`;
/**
 * 'row alignment' CSS Grid styling directive
 * Configures the alignment in the row direction
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-18
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-20
 */
let DefaultGridAlignRowsDirective = /*#__PURE__*/(() => {
  class DefaultGridAlignRowsDirective extends GridAlignRowsDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$9;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridAlignRowsDirective_BaseFactory;
        return function DefaultGridAlignRowsDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridAlignRowsDirective_BaseFactory || (ɵDefaultGridAlignRowsDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridAlignRowsDirective)))(__ngFactoryType__ || DefaultGridAlignRowsDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridAlignRowsDirective,
        selectors: [["", "gdAlignRows", ""], ["", "gdAlignRows.xs", ""], ["", "gdAlignRows.sm", ""], ["", "gdAlignRows.md", ""], ["", "gdAlignRows.lg", ""], ["", "gdAlignRows.xl", ""], ["", "gdAlignRows.lt-sm", ""], ["", "gdAlignRows.lt-md", ""], ["", "gdAlignRows.lt-lg", ""], ["", "gdAlignRows.lt-xl", ""], ["", "gdAlignRows.gt-xs", ""], ["", "gdAlignRows.gt-sm", ""], ["", "gdAlignRows.gt-md", ""], ["", "gdAlignRows.gt-lg", ""]],
        inputs: {
          gdAlignRows: "gdAlignRows",
          "gdAlignRows.xs": "gdAlignRows.xs",
          "gdAlignRows.sm": "gdAlignRows.sm",
          "gdAlignRows.md": "gdAlignRows.md",
          "gdAlignRows.lg": "gdAlignRows.lg",
          "gdAlignRows.xl": "gdAlignRows.xl",
          "gdAlignRows.lt-sm": "gdAlignRows.lt-sm",
          "gdAlignRows.lt-md": "gdAlignRows.lt-md",
          "gdAlignRows.lt-lg": "gdAlignRows.lt-lg",
          "gdAlignRows.lt-xl": "gdAlignRows.lt-xl",
          "gdAlignRows.gt-xs": "gdAlignRows.gt-xs",
          "gdAlignRows.gt-sm": "gdAlignRows.gt-sm",
          "gdAlignRows.gt-md": "gdAlignRows.gt-md",
          "gdAlignRows.gt-lg": "gdAlignRows.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridAlignRowsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function buildCss$1(align, inline) {
  const css = {},
    [mainAxis, crossAxis] = align.split(' ');
  // Main axis
  switch (mainAxis) {
    case 'center':
    case 'space-around':
    case 'space-between':
    case 'space-evenly':
    case 'end':
    case 'start':
    case 'stretch':
      css['justify-content'] = mainAxis;
      break;
    default:
      // default main axis
      css['justify-content'] = DEFAULT_MAIN;
      break;
  }
  // Cross-axis
  switch (crossAxis) {
    case 'start':
    case 'center':
    case 'end':
    case 'stretch':
      css['justify-items'] = crossAxis;
      break;
    default:
      // 'stretch'
      // default cross axis
      css['justify-items'] = DEFAULT_CROSS;
      break;
  }
  css['display'] = inline ? 'inline-grid' : 'grid';
  return css;
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE$7 = 'auto';
let GridAreaStyleBuilder = /*#__PURE__*/(() => {
  class GridAreaStyleBuilder extends StyleBuilder {
    buildStyles(input) {
      return {
        'grid-area': input || DEFAULT_VALUE$7
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridAreaStyleBuilder_BaseFactory;
        return function GridAreaStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridAreaStyleBuilder_BaseFactory || (ɵGridAreaStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridAreaStyleBuilder)))(__ngFactoryType__ || GridAreaStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridAreaStyleBuilder,
        factory: GridAreaStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridAreaStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridAreaDirective = /*#__PURE__*/(() => {
  class GridAreaDirective extends BaseDirective2 {
    constructor(elRef, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.DIRECTIVE_KEY = 'grid-area';
      this.styleCache = gridAreaCache;
      this.init();
    }
    static {
      this.ɵfac = function GridAreaDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridAreaDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(GridAreaStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridAreaDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridAreaDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const gridAreaCache = new Map();
const inputs$8 = ['gdArea', 'gdArea.xs', 'gdArea.sm', 'gdArea.md', 'gdArea.lg', 'gdArea.xl', 'gdArea.lt-sm', 'gdArea.lt-md', 'gdArea.lt-lg', 'gdArea.lt-xl', 'gdArea.gt-xs', 'gdArea.gt-sm', 'gdArea.gt-md', 'gdArea.gt-lg'];
const selector$8 = `
  [gdArea],
  [gdArea.xs], [gdArea.sm], [gdArea.md], [gdArea.lg], [gdArea.xl],
  [gdArea.lt-sm], [gdArea.lt-md], [gdArea.lt-lg], [gdArea.lt-xl],
  [gdArea.gt-xs], [gdArea.gt-sm], [gdArea.gt-md], [gdArea.gt-lg]
`;
/**
 * 'grid-area' CSS Grid styling directive
 * Configures the name or position of an element within the grid
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-27
 */
let DefaultGridAreaDirective = /*#__PURE__*/(() => {
  class DefaultGridAreaDirective extends GridAreaDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$8;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridAreaDirective_BaseFactory;
        return function DefaultGridAreaDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridAreaDirective_BaseFactory || (ɵDefaultGridAreaDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridAreaDirective)))(__ngFactoryType__ || DefaultGridAreaDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridAreaDirective,
        selectors: [["", "gdArea", ""], ["", "gdArea.xs", ""], ["", "gdArea.sm", ""], ["", "gdArea.md", ""], ["", "gdArea.lg", ""], ["", "gdArea.xl", ""], ["", "gdArea.lt-sm", ""], ["", "gdArea.lt-md", ""], ["", "gdArea.lt-lg", ""], ["", "gdArea.lt-xl", ""], ["", "gdArea.gt-xs", ""], ["", "gdArea.gt-sm", ""], ["", "gdArea.gt-md", ""], ["", "gdArea.gt-lg", ""]],
        inputs: {
          gdArea: "gdArea",
          "gdArea.xs": "gdArea.xs",
          "gdArea.sm": "gdArea.sm",
          "gdArea.md": "gdArea.md",
          "gdArea.lg": "gdArea.lg",
          "gdArea.xl": "gdArea.xl",
          "gdArea.lt-sm": "gdArea.lt-sm",
          "gdArea.lt-md": "gdArea.lt-md",
          "gdArea.lt-lg": "gdArea.lt-lg",
          "gdArea.lt-xl": "gdArea.lt-xl",
          "gdArea.gt-xs": "gdArea.gt-xs",
          "gdArea.gt-sm": "gdArea.gt-sm",
          "gdArea.gt-md": "gdArea.gt-md",
          "gdArea.gt-lg": "gdArea.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridAreaDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE$6 = 'none';
const DELIMETER = '|';
let GridAreasStyleBuiler = /*#__PURE__*/(() => {
  class GridAreasStyleBuiler extends StyleBuilder {
    buildStyles(input, parent) {
      const areas = (input || DEFAULT_VALUE$6).split(DELIMETER).map(v => `"${v.trim()}"`);
      return {
        display: parent.inline ? 'inline-grid' : 'grid',
        'grid-template-areas': areas.join(' ')
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridAreasStyleBuiler_BaseFactory;
        return function GridAreasStyleBuiler_Factory(__ngFactoryType__) {
          return (ɵGridAreasStyleBuiler_BaseFactory || (ɵGridAreasStyleBuiler_BaseFactory = i0.ɵɵgetInheritedFactory(GridAreasStyleBuiler)))(__ngFactoryType__ || GridAreasStyleBuiler);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridAreasStyleBuiler,
        factory: GridAreasStyleBuiler.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridAreasStyleBuiler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridAreasDirective = /*#__PURE__*/(() => {
  class GridAreasDirective extends BaseDirective2 {
    get inline() {
      return this._inline;
    }
    set inline(val) {
      this._inline = coerceBooleanProperty(val);
    }
    constructor(elRef, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.DIRECTIVE_KEY = 'grid-areas';
      this._inline = false;
      this.init();
    }
    // *********************************************
    // Protected methods
    // *********************************************
    updateWithValue(value) {
      this.styleCache = this.inline ? areasInlineCache : areasCache;
      this.addStyles(value, {
        inline: this.inline
      });
    }
    static {
      this.ɵfac = function GridAreasDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridAreasDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(GridAreasStyleBuiler), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridAreasDirective,
        inputs: {
          inline: [0, "gdInline", "inline"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridAreasDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const areasCache = new Map();
const areasInlineCache = new Map();
const inputs$7 = ['gdAreas', 'gdAreas.xs', 'gdAreas.sm', 'gdAreas.md', 'gdAreas.lg', 'gdAreas.xl', 'gdAreas.lt-sm', 'gdAreas.lt-md', 'gdAreas.lt-lg', 'gdAreas.lt-xl', 'gdAreas.gt-xs', 'gdAreas.gt-sm', 'gdAreas.gt-md', 'gdAreas.gt-lg'];
const selector$7 = `
  [gdAreas],
  [gdAreas.xs], [gdAreas.sm], [gdAreas.md], [gdAreas.lg], [gdAreas.xl],
  [gdAreas.lt-sm], [gdAreas.lt-md], [gdAreas.lt-lg], [gdAreas.lt-xl],
  [gdAreas.gt-xs], [gdAreas.gt-sm], [gdAreas.gt-md], [gdAreas.gt-lg]
`;
/**
 * 'grid-template-areas' CSS Grid styling directive
 * Configures the names of elements within the grid
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-14
 */
let DefaultGridAreasDirective = /*#__PURE__*/(() => {
  class DefaultGridAreasDirective extends GridAreasDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$7;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridAreasDirective_BaseFactory;
        return function DefaultGridAreasDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridAreasDirective_BaseFactory || (ɵDefaultGridAreasDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridAreasDirective)))(__ngFactoryType__ || DefaultGridAreasDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridAreasDirective,
        selectors: [["", "gdAreas", ""], ["", "gdAreas.xs", ""], ["", "gdAreas.sm", ""], ["", "gdAreas.md", ""], ["", "gdAreas.lg", ""], ["", "gdAreas.xl", ""], ["", "gdAreas.lt-sm", ""], ["", "gdAreas.lt-md", ""], ["", "gdAreas.lt-lg", ""], ["", "gdAreas.lt-xl", ""], ["", "gdAreas.gt-xs", ""], ["", "gdAreas.gt-sm", ""], ["", "gdAreas.gt-md", ""], ["", "gdAreas.gt-lg", ""]],
        inputs: {
          gdAreas: "gdAreas",
          "gdAreas.xs": "gdAreas.xs",
          "gdAreas.sm": "gdAreas.sm",
          "gdAreas.md": "gdAreas.md",
          "gdAreas.lg": "gdAreas.lg",
          "gdAreas.xl": "gdAreas.xl",
          "gdAreas.lt-sm": "gdAreas.lt-sm",
          "gdAreas.lt-md": "gdAreas.lt-md",
          "gdAreas.lt-lg": "gdAreas.lt-lg",
          "gdAreas.lt-xl": "gdAreas.lt-xl",
          "gdAreas.gt-xs": "gdAreas.gt-xs",
          "gdAreas.gt-sm": "gdAreas.gt-sm",
          "gdAreas.gt-md": "gdAreas.gt-md",
          "gdAreas.gt-lg": "gdAreas.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridAreasDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE$5 = 'initial';
let GridAutoStyleBuilder = /*#__PURE__*/(() => {
  class GridAutoStyleBuilder extends StyleBuilder {
    buildStyles(input, parent) {
      let [direction, dense] = (input || DEFAULT_VALUE$5).split(' ');
      if (direction !== 'column' && direction !== 'row' && direction !== 'dense') {
        direction = 'row';
      }
      dense = dense === 'dense' && direction !== 'dense' ? ' dense' : '';
      return {
        display: parent.inline ? 'inline-grid' : 'grid',
        'grid-auto-flow': direction + dense
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridAutoStyleBuilder_BaseFactory;
        return function GridAutoStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridAutoStyleBuilder_BaseFactory || (ɵGridAutoStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridAutoStyleBuilder)))(__ngFactoryType__ || GridAutoStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridAutoStyleBuilder,
        factory: GridAutoStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridAutoStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridAutoDirective = /*#__PURE__*/(() => {
  class GridAutoDirective extends BaseDirective2 {
    get inline() {
      return this._inline;
    }
    set inline(val) {
      this._inline = coerceBooleanProperty(val);
    }
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this._inline = false;
      this.DIRECTIVE_KEY = 'grid-auto';
      this.init();
    }
    // *********************************************
    // Protected methods
    // *********************************************
    updateWithValue(value) {
      this.styleCache = this.inline ? autoInlineCache : autoCache;
      this.addStyles(value, {
        inline: this.inline
      });
    }
    static {
      this.ɵfac = function GridAutoDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridAutoDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridAutoStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridAutoDirective,
        inputs: {
          inline: [0, "gdInline", "inline"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridAutoDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const autoCache = new Map();
const autoInlineCache = new Map();
const inputs$6 = ['gdAuto', 'gdAuto.xs', 'gdAuto.sm', 'gdAuto.md', 'gdAuto.lg', 'gdAuto.xl', 'gdAuto.lt-sm', 'gdAuto.lt-md', 'gdAuto.lt-lg', 'gdAuto.lt-xl', 'gdAuto.gt-xs', 'gdAuto.gt-sm', 'gdAuto.gt-md', 'gdAuto.gt-lg'];
const selector$6 = `
  [gdAuto],
  [gdAuto.xs], [gdAuto.sm], [gdAuto.md], [gdAuto.lg], [gdAuto.xl],
  [gdAuto.lt-sm], [gdAuto.lt-md], [gdAuto.lt-lg], [gdAuto.lt-xl],
  [gdAuto.gt-xs], [gdAuto.gt-sm], [gdAuto.gt-md], [gdAuto.gt-lg]
`;
/**
 * 'grid-auto-flow' CSS Grid styling directive
 * Configures the auto placement algorithm for the grid
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-23
 */
let DefaultGridAutoDirective = /*#__PURE__*/(() => {
  class DefaultGridAutoDirective extends GridAutoDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$6;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridAutoDirective_BaseFactory;
        return function DefaultGridAutoDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridAutoDirective_BaseFactory || (ɵDefaultGridAutoDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridAutoDirective)))(__ngFactoryType__ || DefaultGridAutoDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridAutoDirective,
        selectors: [["", "gdAuto", ""], ["", "gdAuto.xs", ""], ["", "gdAuto.sm", ""], ["", "gdAuto.md", ""], ["", "gdAuto.lg", ""], ["", "gdAuto.xl", ""], ["", "gdAuto.lt-sm", ""], ["", "gdAuto.lt-md", ""], ["", "gdAuto.lt-lg", ""], ["", "gdAuto.lt-xl", ""], ["", "gdAuto.gt-xs", ""], ["", "gdAuto.gt-sm", ""], ["", "gdAuto.gt-md", ""], ["", "gdAuto.gt-lg", ""]],
        inputs: {
          gdAuto: "gdAuto",
          "gdAuto.xs": "gdAuto.xs",
          "gdAuto.sm": "gdAuto.sm",
          "gdAuto.md": "gdAuto.md",
          "gdAuto.lg": "gdAuto.lg",
          "gdAuto.xl": "gdAuto.xl",
          "gdAuto.lt-sm": "gdAuto.lt-sm",
          "gdAuto.lt-md": "gdAuto.lt-md",
          "gdAuto.lt-lg": "gdAuto.lt-lg",
          "gdAuto.lt-xl": "gdAuto.lt-xl",
          "gdAuto.gt-xs": "gdAuto.gt-xs",
          "gdAuto.gt-sm": "gdAuto.gt-sm",
          "gdAuto.gt-md": "gdAuto.gt-md",
          "gdAuto.gt-lg": "gdAuto.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridAutoDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE$4 = 'auto';
let GridColumnStyleBuilder = /*#__PURE__*/(() => {
  class GridColumnStyleBuilder extends StyleBuilder {
    buildStyles(input) {
      return {
        'grid-column': input || DEFAULT_VALUE$4
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridColumnStyleBuilder_BaseFactory;
        return function GridColumnStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridColumnStyleBuilder_BaseFactory || (ɵGridColumnStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridColumnStyleBuilder)))(__ngFactoryType__ || GridColumnStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridColumnStyleBuilder,
        factory: GridColumnStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridColumnStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridColumnDirective = /*#__PURE__*/(() => {
  class GridColumnDirective extends BaseDirective2 {
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this.DIRECTIVE_KEY = 'grid-column';
      this.styleCache = columnCache;
      this.init();
    }
    static {
      this.ɵfac = function GridColumnDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridColumnDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridColumnStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridColumnDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridColumnDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const columnCache = new Map();
const inputs$5 = ['gdColumn', 'gdColumn.xs', 'gdColumn.sm', 'gdColumn.md', 'gdColumn.lg', 'gdColumn.xl', 'gdColumn.lt-sm', 'gdColumn.lt-md', 'gdColumn.lt-lg', 'gdColumn.lt-xl', 'gdColumn.gt-xs', 'gdColumn.gt-sm', 'gdColumn.gt-md', 'gdColumn.gt-lg'];
const selector$5 = `
  [gdColumn],
  [gdColumn.xs], [gdColumn.sm], [gdColumn.md], [gdColumn.lg], [gdColumn.xl],
  [gdColumn.lt-sm], [gdColumn.lt-md], [gdColumn.lt-lg], [gdColumn.lt-xl],
  [gdColumn.gt-xs], [gdColumn.gt-sm], [gdColumn.gt-md], [gdColumn.gt-lg]
`;
/**
 * 'grid-column' CSS Grid styling directive
 * Configures the name or position of an element within the grid
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-26
 */
let DefaultGridColumnDirective = /*#__PURE__*/(() => {
  class DefaultGridColumnDirective extends GridColumnDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$5;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridColumnDirective_BaseFactory;
        return function DefaultGridColumnDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridColumnDirective_BaseFactory || (ɵDefaultGridColumnDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridColumnDirective)))(__ngFactoryType__ || DefaultGridColumnDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridColumnDirective,
        selectors: [["", "gdColumn", ""], ["", "gdColumn.xs", ""], ["", "gdColumn.sm", ""], ["", "gdColumn.md", ""], ["", "gdColumn.lg", ""], ["", "gdColumn.xl", ""], ["", "gdColumn.lt-sm", ""], ["", "gdColumn.lt-md", ""], ["", "gdColumn.lt-lg", ""], ["", "gdColumn.lt-xl", ""], ["", "gdColumn.gt-xs", ""], ["", "gdColumn.gt-sm", ""], ["", "gdColumn.gt-md", ""], ["", "gdColumn.gt-lg", ""]],
        inputs: {
          gdColumn: "gdColumn",
          "gdColumn.xs": "gdColumn.xs",
          "gdColumn.sm": "gdColumn.sm",
          "gdColumn.md": "gdColumn.md",
          "gdColumn.lg": "gdColumn.lg",
          "gdColumn.xl": "gdColumn.xl",
          "gdColumn.lt-sm": "gdColumn.lt-sm",
          "gdColumn.lt-md": "gdColumn.lt-md",
          "gdColumn.lt-lg": "gdColumn.lt-lg",
          "gdColumn.lt-xl": "gdColumn.lt-xl",
          "gdColumn.gt-xs": "gdColumn.gt-xs",
          "gdColumn.gt-sm": "gdColumn.gt-sm",
          "gdColumn.gt-md": "gdColumn.gt-md",
          "gdColumn.gt-lg": "gdColumn.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridColumnDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE$3 = 'none';
const AUTO_SPECIFIER$1 = '!';
let GridColumnsStyleBuilder = /*#__PURE__*/(() => {
  class GridColumnsStyleBuilder extends StyleBuilder {
    buildStyles(input, parent) {
      input = input || DEFAULT_VALUE$3;
      let auto = false;
      if (input.endsWith(AUTO_SPECIFIER$1)) {
        input = input.substring(0, input.indexOf(AUTO_SPECIFIER$1));
        auto = true;
      }
      const css = {
        display: parent.inline ? 'inline-grid' : 'grid',
        'grid-auto-columns': '',
        'grid-template-columns': ''
      };
      const key = auto ? 'grid-auto-columns' : 'grid-template-columns';
      css[key] = input;
      return css;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridColumnsStyleBuilder_BaseFactory;
        return function GridColumnsStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridColumnsStyleBuilder_BaseFactory || (ɵGridColumnsStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridColumnsStyleBuilder)))(__ngFactoryType__ || GridColumnsStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridColumnsStyleBuilder,
        factory: GridColumnsStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridColumnsStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridColumnsDirective = /*#__PURE__*/(() => {
  class GridColumnsDirective extends BaseDirective2 {
    get inline() {
      return this._inline;
    }
    set inline(val) {
      this._inline = coerceBooleanProperty(val);
    }
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this.DIRECTIVE_KEY = 'grid-columns';
      this._inline = false;
      this.init();
    }
    // *********************************************
    // Protected methods
    // *********************************************
    updateWithValue(value) {
      this.styleCache = this.inline ? columnsInlineCache : columnsCache;
      this.addStyles(value, {
        inline: this.inline
      });
    }
    static {
      this.ɵfac = function GridColumnsDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridColumnsDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridColumnsStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridColumnsDirective,
        inputs: {
          inline: [0, "gdInline", "inline"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridColumnsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const columnsCache = new Map();
const columnsInlineCache = new Map();
const inputs$4 = ['gdColumns', 'gdColumns.xs', 'gdColumns.sm', 'gdColumns.md', 'gdColumns.lg', 'gdColumns.xl', 'gdColumns.lt-sm', 'gdColumns.lt-md', 'gdColumns.lt-lg', 'gdColumns.lt-xl', 'gdColumns.gt-xs', 'gdColumns.gt-sm', 'gdColumns.gt-md', 'gdColumns.gt-lg'];
const selector$4 = `
  [gdColumns],
  [gdColumns.xs], [gdColumns.sm], [gdColumns.md], [gdColumns.lg], [gdColumns.xl],
  [gdColumns.lt-sm], [gdColumns.lt-md], [gdColumns.lt-lg], [gdColumns.lt-xl],
  [gdColumns.gt-xs], [gdColumns.gt-sm], [gdColumns.gt-md], [gdColumns.gt-lg]
`;
/**
 * 'grid-template-columns' CSS Grid styling directive
 * Configures the sizing for the columns in the grid
 * Syntax: <column value> [auto]
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-13
 */
let DefaultGridColumnsDirective = /*#__PURE__*/(() => {
  class DefaultGridColumnsDirective extends GridColumnsDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$4;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridColumnsDirective_BaseFactory;
        return function DefaultGridColumnsDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridColumnsDirective_BaseFactory || (ɵDefaultGridColumnsDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridColumnsDirective)))(__ngFactoryType__ || DefaultGridColumnsDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridColumnsDirective,
        selectors: [["", "gdColumns", ""], ["", "gdColumns.xs", ""], ["", "gdColumns.sm", ""], ["", "gdColumns.md", ""], ["", "gdColumns.lg", ""], ["", "gdColumns.xl", ""], ["", "gdColumns.lt-sm", ""], ["", "gdColumns.lt-md", ""], ["", "gdColumns.lt-lg", ""], ["", "gdColumns.lt-xl", ""], ["", "gdColumns.gt-xs", ""], ["", "gdColumns.gt-sm", ""], ["", "gdColumns.gt-md", ""], ["", "gdColumns.gt-lg", ""]],
        inputs: {
          gdColumns: "gdColumns",
          "gdColumns.xs": "gdColumns.xs",
          "gdColumns.sm": "gdColumns.sm",
          "gdColumns.md": "gdColumns.md",
          "gdColumns.lg": "gdColumns.lg",
          "gdColumns.xl": "gdColumns.xl",
          "gdColumns.lt-sm": "gdColumns.lt-sm",
          "gdColumns.lt-md": "gdColumns.lt-md",
          "gdColumns.lt-lg": "gdColumns.lt-lg",
          "gdColumns.lt-xl": "gdColumns.lt-xl",
          "gdColumns.gt-xs": "gdColumns.gt-xs",
          "gdColumns.gt-sm": "gdColumns.gt-sm",
          "gdColumns.gt-md": "gdColumns.gt-md",
          "gdColumns.gt-lg": "gdColumns.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridColumnsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE$2 = '0';
let GridGapStyleBuilder = /*#__PURE__*/(() => {
  class GridGapStyleBuilder extends StyleBuilder {
    buildStyles(input, parent) {
      return {
        display: parent.inline ? 'inline-grid' : 'grid',
        'grid-gap': input || DEFAULT_VALUE$2
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridGapStyleBuilder_BaseFactory;
        return function GridGapStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridGapStyleBuilder_BaseFactory || (ɵGridGapStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridGapStyleBuilder)))(__ngFactoryType__ || GridGapStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridGapStyleBuilder,
        factory: GridGapStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridGapStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridGapDirective = /*#__PURE__*/(() => {
  class GridGapDirective extends BaseDirective2 {
    get inline() {
      return this._inline;
    }
    set inline(val) {
      this._inline = coerceBooleanProperty(val);
    }
    constructor(elRef, styleUtils, styleBuilder, marshal) {
      super(elRef, styleBuilder, styleUtils, marshal);
      this.DIRECTIVE_KEY = 'grid-gap';
      this._inline = false;
      this.init();
    }
    // *********************************************
    // Protected methods
    // *********************************************
    updateWithValue(value) {
      this.styleCache = this.inline ? gapInlineCache : gapCache;
      this.addStyles(value, {
        inline: this.inline
      });
    }
    static {
      this.ɵfac = function GridGapDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridGapDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(GridGapStyleBuilder), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridGapDirective,
        inputs: {
          inline: [0, "gdInline", "inline"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridGapDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const gapCache = new Map();
const gapInlineCache = new Map();
const inputs$3 = ['gdGap', 'gdGap.xs', 'gdGap.sm', 'gdGap.md', 'gdGap.lg', 'gdGap.xl', 'gdGap.lt-sm', 'gdGap.lt-md', 'gdGap.lt-lg', 'gdGap.lt-xl', 'gdGap.gt-xs', 'gdGap.gt-sm', 'gdGap.gt-md', 'gdGap.gt-lg'];
const selector$3 = `
  [gdGap],
  [gdGap.xs], [gdGap.sm], [gdGap.md], [gdGap.lg], [gdGap.xl],
  [gdGap.lt-sm], [gdGap.lt-md], [gdGap.lt-lg], [gdGap.lt-xl],
  [gdGap.gt-xs], [gdGap.gt-sm], [gdGap.gt-md], [gdGap.gt-lg]
`;
/**
 * 'grid-gap' CSS Grid styling directive
 * Configures the gap between items in the grid
 * Syntax: <row gap> [<column-gap>]
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-17
 */
let DefaultGridGapDirective = /*#__PURE__*/(() => {
  class DefaultGridGapDirective extends GridGapDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$3;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridGapDirective_BaseFactory;
        return function DefaultGridGapDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridGapDirective_BaseFactory || (ɵDefaultGridGapDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridGapDirective)))(__ngFactoryType__ || DefaultGridGapDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridGapDirective,
        selectors: [["", "gdGap", ""], ["", "gdGap.xs", ""], ["", "gdGap.sm", ""], ["", "gdGap.md", ""], ["", "gdGap.lg", ""], ["", "gdGap.xl", ""], ["", "gdGap.lt-sm", ""], ["", "gdGap.lt-md", ""], ["", "gdGap.lt-lg", ""], ["", "gdGap.lt-xl", ""], ["", "gdGap.gt-xs", ""], ["", "gdGap.gt-sm", ""], ["", "gdGap.gt-md", ""], ["", "gdGap.gt-lg", ""]],
        inputs: {
          gdGap: "gdGap",
          "gdGap.xs": "gdGap.xs",
          "gdGap.sm": "gdGap.sm",
          "gdGap.md": "gdGap.md",
          "gdGap.lg": "gdGap.lg",
          "gdGap.xl": "gdGap.xl",
          "gdGap.lt-sm": "gdGap.lt-sm",
          "gdGap.lt-md": "gdGap.lt-md",
          "gdGap.lt-lg": "gdGap.lt-lg",
          "gdGap.lt-xl": "gdGap.lt-xl",
          "gdGap.gt-xs": "gdGap.gt-xs",
          "gdGap.gt-sm": "gdGap.gt-sm",
          "gdGap.gt-md": "gdGap.gt-md",
          "gdGap.gt-lg": "gdGap.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridGapDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const ROW_DEFAULT = 'stretch';
const COL_DEFAULT = 'stretch';
let GridAlignStyleBuilder = /*#__PURE__*/(() => {
  class GridAlignStyleBuilder extends StyleBuilder {
    buildStyles(input) {
      return buildCss(input || ROW_DEFAULT);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridAlignStyleBuilder_BaseFactory;
        return function GridAlignStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridAlignStyleBuilder_BaseFactory || (ɵGridAlignStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridAlignStyleBuilder)))(__ngFactoryType__ || GridAlignStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridAlignStyleBuilder,
        factory: GridAlignStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridAlignStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridAlignDirective = /*#__PURE__*/(() => {
  class GridAlignDirective extends BaseDirective2 {
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this.DIRECTIVE_KEY = 'grid-align';
      this.styleCache = alignCache;
      this.init();
    }
    static {
      this.ɵfac = function GridAlignDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridAlignDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridAlignStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridAlignDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridAlignDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const alignCache = new Map();
const inputs$2 = ['gdGridAlign', 'gdGridAlign.xs', 'gdGridAlign.sm', 'gdGridAlign.md', 'gdGridAlign.lg', 'gdGridAlign.xl', 'gdGridAlign.lt-sm', 'gdGridAlign.lt-md', 'gdGridAlign.lt-lg', 'gdGridAlign.lt-xl', 'gdGridAlign.gt-xs', 'gdGridAlign.gt-sm', 'gdGridAlign.gt-md', 'gdGridAlign.gt-lg'];
const selector$2 = `
  [gdGridAlign],
  [gdGridAlign.xs], [gdGridAlign.sm], [gdGridAlign.md], [gdGridAlign.lg],[gdGridAlign.xl],
  [gdGridAlign.lt-sm], [gdGridAlign.lt-md], [gdGridAlign.lt-lg], [gdGridAlign.lt-xl],
  [gdGridAlign.gt-xs], [gdGridAlign.gt-sm], [gdGridAlign.gt-md], [gdGridAlign.gt-lg]
`;
/**
 * 'align' CSS Grid styling directive for grid children
 *  Defines positioning of child elements along row and column axis in a grid container
 *  Optional values: {row-axis} values or {row-axis column-axis} value pairs
 *
 *  @see https://css-tricks.com/snippets/css/complete-guide-grid/#prop-justify-self
 *  @see https://css-tricks.com/snippets/css/complete-guide-grid/#prop-align-self
 */
let DefaultGridAlignDirective = /*#__PURE__*/(() => {
  class DefaultGridAlignDirective extends GridAlignDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$2;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridAlignDirective_BaseFactory;
        return function DefaultGridAlignDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridAlignDirective_BaseFactory || (ɵDefaultGridAlignDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridAlignDirective)))(__ngFactoryType__ || DefaultGridAlignDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridAlignDirective,
        selectors: [["", "gdGridAlign", ""], ["", "gdGridAlign.xs", ""], ["", "gdGridAlign.sm", ""], ["", "gdGridAlign.md", ""], ["", "gdGridAlign.lg", ""], ["", "gdGridAlign.xl", ""], ["", "gdGridAlign.lt-sm", ""], ["", "gdGridAlign.lt-md", ""], ["", "gdGridAlign.lt-lg", ""], ["", "gdGridAlign.lt-xl", ""], ["", "gdGridAlign.gt-xs", ""], ["", "gdGridAlign.gt-sm", ""], ["", "gdGridAlign.gt-md", ""], ["", "gdGridAlign.gt-lg", ""]],
        inputs: {
          gdGridAlign: "gdGridAlign",
          "gdGridAlign.xs": "gdGridAlign.xs",
          "gdGridAlign.sm": "gdGridAlign.sm",
          "gdGridAlign.md": "gdGridAlign.md",
          "gdGridAlign.lg": "gdGridAlign.lg",
          "gdGridAlign.xl": "gdGridAlign.xl",
          "gdGridAlign.lt-sm": "gdGridAlign.lt-sm",
          "gdGridAlign.lt-md": "gdGridAlign.lt-md",
          "gdGridAlign.lt-lg": "gdGridAlign.lt-lg",
          "gdGridAlign.lt-xl": "gdGridAlign.lt-xl",
          "gdGridAlign.gt-xs": "gdGridAlign.gt-xs",
          "gdGridAlign.gt-sm": "gdGridAlign.gt-sm",
          "gdGridAlign.gt-md": "gdGridAlign.gt-md",
          "gdGridAlign.gt-lg": "gdGridAlign.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridAlignDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function buildCss(align = '') {
  const css = {},
    [rowAxis, columnAxis] = align.split(' ');
  // Row axis
  switch (rowAxis) {
    case 'end':
      css['justify-self'] = 'end';
      break;
    case 'center':
      css['justify-self'] = 'center';
      break;
    case 'stretch':
      css['justify-self'] = 'stretch';
      break;
    case 'start':
      css['justify-self'] = 'start';
      break;
    default:
      // default row axis
      css['justify-self'] = ROW_DEFAULT;
      break;
  }
  // Column axis
  switch (columnAxis) {
    case 'end':
      css['align-self'] = 'end';
      break;
    case 'center':
      css['align-self'] = 'center';
      break;
    case 'stretch':
      css['align-self'] = 'stretch';
      break;
    case 'start':
      css['align-self'] = 'start';
      break;
    default:
      // default column axis
      css['align-self'] = COL_DEFAULT;
      break;
  }
  return css;
}

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE$1 = 'auto';
let GridRowStyleBuilder = /*#__PURE__*/(() => {
  class GridRowStyleBuilder extends StyleBuilder {
    buildStyles(input) {
      return {
        'grid-row': input || DEFAULT_VALUE$1
      };
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridRowStyleBuilder_BaseFactory;
        return function GridRowStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridRowStyleBuilder_BaseFactory || (ɵGridRowStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridRowStyleBuilder)))(__ngFactoryType__ || GridRowStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridRowStyleBuilder,
        factory: GridRowStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridRowStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridRowDirective = /*#__PURE__*/(() => {
  class GridRowDirective extends BaseDirective2 {
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this.DIRECTIVE_KEY = 'grid-row';
      this.styleCache = rowCache;
      this.init();
    }
    static {
      this.ɵfac = function GridRowDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridRowDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridRowStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridRowDirective,
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridRowDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const rowCache = new Map();
const inputs$1 = ['gdRow', 'gdRow.xs', 'gdRow.sm', 'gdRow.md', 'gdRow.lg', 'gdRow.xl', 'gdRow.lt-sm', 'gdRow.lt-md', 'gdRow.lt-lg', 'gdRow.lt-xl', 'gdRow.gt-xs', 'gdRow.gt-sm', 'gdRow.gt-md', 'gdRow.gt-lg'];
const selector$1 = `
  [gdRow],
  [gdRow.xs], [gdRow.sm], [gdRow.md], [gdRow.lg], [gdRow.xl],
  [gdRow.lt-sm], [gdRow.lt-md], [gdRow.lt-lg], [gdRow.lt-xl],
  [gdRow.gt-xs], [gdRow.gt-sm], [gdRow.gt-md], [gdRow.gt-lg]
`;
/**
 * 'grid-row' CSS Grid styling directive
 * Configures the name or position of an element within the grid
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-26
 */
let DefaultGridRowDirective = /*#__PURE__*/(() => {
  class DefaultGridRowDirective extends GridRowDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs$1;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridRowDirective_BaseFactory;
        return function DefaultGridRowDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridRowDirective_BaseFactory || (ɵDefaultGridRowDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridRowDirective)))(__ngFactoryType__ || DefaultGridRowDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridRowDirective,
        selectors: [["", "gdRow", ""], ["", "gdRow.xs", ""], ["", "gdRow.sm", ""], ["", "gdRow.md", ""], ["", "gdRow.lg", ""], ["", "gdRow.xl", ""], ["", "gdRow.lt-sm", ""], ["", "gdRow.lt-md", ""], ["", "gdRow.lt-lg", ""], ["", "gdRow.lt-xl", ""], ["", "gdRow.gt-xs", ""], ["", "gdRow.gt-sm", ""], ["", "gdRow.gt-md", ""], ["", "gdRow.gt-lg", ""]],
        inputs: {
          gdRow: "gdRow",
          "gdRow.xs": "gdRow.xs",
          "gdRow.sm": "gdRow.sm",
          "gdRow.md": "gdRow.md",
          "gdRow.lg": "gdRow.lg",
          "gdRow.xl": "gdRow.xl",
          "gdRow.lt-sm": "gdRow.lt-sm",
          "gdRow.lt-md": "gdRow.lt-md",
          "gdRow.lt-lg": "gdRow.lt-lg",
          "gdRow.lt-xl": "gdRow.lt-xl",
          "gdRow.gt-xs": "gdRow.gt-xs",
          "gdRow.gt-sm": "gdRow.gt-sm",
          "gdRow.gt-md": "gdRow.gt-md",
          "gdRow.gt-lg": "gdRow.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridRowDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const DEFAULT_VALUE = 'none';
const AUTO_SPECIFIER = '!';
let GridRowsStyleBuilder = /*#__PURE__*/(() => {
  class GridRowsStyleBuilder extends StyleBuilder {
    buildStyles(input, parent) {
      input = input || DEFAULT_VALUE;
      let auto = false;
      if (input.endsWith(AUTO_SPECIFIER)) {
        input = input.substring(0, input.indexOf(AUTO_SPECIFIER));
        auto = true;
      }
      const css = {
        display: parent.inline ? 'inline-grid' : 'grid',
        'grid-auto-rows': '',
        'grid-template-rows': ''
      };
      const key = auto ? 'grid-auto-rows' : 'grid-template-rows';
      css[key] = input;
      return css;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵGridRowsStyleBuilder_BaseFactory;
        return function GridRowsStyleBuilder_Factory(__ngFactoryType__) {
          return (ɵGridRowsStyleBuilder_BaseFactory || (ɵGridRowsStyleBuilder_BaseFactory = i0.ɵɵgetInheritedFactory(GridRowsStyleBuilder)))(__ngFactoryType__ || GridRowsStyleBuilder);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GridRowsStyleBuilder,
        factory: GridRowsStyleBuilder.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GridRowsStyleBuilder;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GridRowsDirective = /*#__PURE__*/(() => {
  class GridRowsDirective extends BaseDirective2 {
    get inline() {
      return this._inline;
    }
    set inline(val) {
      this._inline = coerceBooleanProperty(val);
    }
    constructor(elementRef, styleBuilder, styler, marshal) {
      super(elementRef, styleBuilder, styler, marshal);
      this.DIRECTIVE_KEY = 'grid-rows';
      this._inline = false;
      this.init();
    }
    // *********************************************
    // Protected methods
    // *********************************************
    updateWithValue(value) {
      this.styleCache = this.inline ? rowsInlineCache : rowsCache;
      this.addStyles(value, {
        inline: this.inline
      });
    }
    static {
      this.ɵfac = function GridRowsDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridRowsDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(GridRowsStyleBuilder), i0.ɵɵdirectiveInject(i1.StyleUtils), i0.ɵɵdirectiveInject(i1.MediaMarshaller));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: GridRowsDirective,
        inputs: {
          inline: [0, "gdInline", "inline"]
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return GridRowsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const rowsCache = new Map();
const rowsInlineCache = new Map();
const inputs = ['gdRows', 'gdRows.xs', 'gdRows.sm', 'gdRows.md', 'gdRows.lg', 'gdRows.xl', 'gdRows.lt-sm', 'gdRows.lt-md', 'gdRows.lt-lg', 'gdRows.lt-xl', 'gdRows.gt-xs', 'gdRows.gt-sm', 'gdRows.gt-md', 'gdRows.gt-lg'];
const selector = `
  [gdRows],
  [gdRows.xs], [gdRows.sm], [gdRows.md], [gdRows.lg], [gdRows.xl],
  [gdRows.lt-sm], [gdRows.lt-md], [gdRows.lt-lg], [gdRows.lt-xl],
  [gdRows.gt-xs], [gdRows.gt-sm], [gdRows.gt-md], [gdRows.gt-lg]
`;
/**
 * 'grid-template-rows' CSS Grid styling directive
 * Configures the sizing for the rows in the grid
 * Syntax: <column value> [auto]
 * @see https://css-tricks.com/snippets/css/complete-guide-grid/#article-header-id-13
 */
let DefaultGridRowsDirective = /*#__PURE__*/(() => {
  class DefaultGridRowsDirective extends GridRowsDirective {
    constructor() {
      super(...arguments);
      this.inputs = inputs;
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵDefaultGridRowsDirective_BaseFactory;
        return function DefaultGridRowsDirective_Factory(__ngFactoryType__) {
          return (ɵDefaultGridRowsDirective_BaseFactory || (ɵDefaultGridRowsDirective_BaseFactory = i0.ɵɵgetInheritedFactory(DefaultGridRowsDirective)))(__ngFactoryType__ || DefaultGridRowsDirective);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: DefaultGridRowsDirective,
        selectors: [["", "gdRows", ""], ["", "gdRows.xs", ""], ["", "gdRows.sm", ""], ["", "gdRows.md", ""], ["", "gdRows.lg", ""], ["", "gdRows.xl", ""], ["", "gdRows.lt-sm", ""], ["", "gdRows.lt-md", ""], ["", "gdRows.lt-lg", ""], ["", "gdRows.lt-xl", ""], ["", "gdRows.gt-xs", ""], ["", "gdRows.gt-sm", ""], ["", "gdRows.gt-md", ""], ["", "gdRows.gt-lg", ""]],
        inputs: {
          gdRows: "gdRows",
          "gdRows.xs": "gdRows.xs",
          "gdRows.sm": "gdRows.sm",
          "gdRows.md": "gdRows.md",
          "gdRows.lg": "gdRows.lg",
          "gdRows.xl": "gdRows.xl",
          "gdRows.lt-sm": "gdRows.lt-sm",
          "gdRows.lt-md": "gdRows.lt-md",
          "gdRows.lt-lg": "gdRows.lt-lg",
          "gdRows.lt-xl": "gdRows.lt-xl",
          "gdRows.gt-xs": "gdRows.gt-xs",
          "gdRows.gt-sm": "gdRows.gt-sm",
          "gdRows.gt-md": "gdRows.gt-md",
          "gdRows.gt-lg": "gdRows.gt-lg"
        },
        features: [i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return DefaultGridRowsDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const ALL_DIRECTIVES = [DefaultGridAlignDirective, DefaultGridAlignColumnsDirective, DefaultGridAlignRowsDirective, DefaultGridAreaDirective, DefaultGridAreasDirective, DefaultGridAutoDirective, DefaultGridColumnDirective, DefaultGridColumnsDirective, DefaultGridGapDirective, DefaultGridRowDirective, DefaultGridRowsDirective];
/**
 * *****************************************************************
 * Define module for the CSS Grid API
 * *****************************************************************
 */
let GridModule = /*#__PURE__*/(() => {
  class GridModule {
    static {
      this.ɵfac = function GridModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GridModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: GridModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [CoreModule]
      });
    }
  }
  return GridModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DefaultGridAlignColumnsDirective, DefaultGridAlignDirective, DefaultGridAlignRowsDirective, DefaultGridAreaDirective, DefaultGridAreasDirective, DefaultGridAutoDirective, DefaultGridColumnDirective, DefaultGridColumnsDirective, DefaultGridGapDirective, DefaultGridRowDirective, DefaultGridRowsDirective, GridAlignColumnsDirective, GridAlignColumnsStyleBuilder, GridAlignDirective, GridAlignRowsDirective, GridAlignRowsStyleBuilder, GridAlignStyleBuilder, GridAreaDirective, GridAreaStyleBuilder, GridAreasDirective, GridAreasStyleBuiler, GridAutoDirective, GridAutoStyleBuilder, GridColumnDirective, GridColumnStyleBuilder, GridColumnsDirective, GridColumnsStyleBuilder, GridGapDirective, GridGapStyleBuilder, GridModule, GridRowDirective, GridRowStyleBuilder, GridRowsDirective, GridRowsStyleBuilder };
